import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { getErrorMessage } from '@core/api/error-handler';
import { captureApiErrorInPostHog } from '@core/api/capture-api-error-in-posthog';
import { useAuthStore } from '@core/store/auth-store';
import { apiClient } from '@core/api/request/api-client';
import { fetchRefreshToken } from '@core/api/fetch-refresh-token';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null
) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

export const responseErrorInterceptor = async (error: AxiosError) => {
  const originalRequest =
    error.config as unknown as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

  if (error.response?.status === 401 && !originalRequest._retry) {
    if (isRefreshing) {
      return new Promise<string | null>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => {
          return apiClient(originalRequest);
        })
        .catch((err) => Promise.reject(getErrorMessage(err)));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const accessToken = await fetchRefreshToken();

      if (accessToken) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        useAuthStore.getState().setAccessToken(accessToken);
        processQueue(null);
        return apiClient(originalRequest);
      } else {
        processQueue(error);
        return Promise.reject(error);
      }
    } catch (refreshError) {
      processQueue(refreshError as AxiosError);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  captureApiErrorInPostHog(error);
  return Promise.reject(getErrorMessage(error));
};
